import { auth } from './config'; 
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut as fbSignOut, 
  onAuthStateChanged as fbOnAuthStateChanged, 
  onIdTokenChanged as fbOnIdTokenChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  applyActionCode,
  checkActionCode,
  ActionCodeSettings,
  User
} from 'firebase/auth';

export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  try {
    await signInWithPopup(auth, provider);
    console.log("Signed in with Google successfully!");
  } catch (error) {
    console.error("Error signing in with Google:", error);
    if (error.code === 'auth/popup-closed-by-user') {
      console.log("User closed the popup");
    }
  }
}

export async function signOut() {
  try {
    await fbSignOut(auth);
    console.log("Signed out successfully!");
  } catch (error) {
    console.error("Error signing out:", error);
  }
}

// Listen for authentication state changes (useful for updating UI)
export function onAuthStateChanged(callback) {
  return fbOnAuthStateChanged(auth, callback);
}

// Listen for ID token changes (useful for server-side validation or refreshing tokens)
export function onIdTokenChanged(callback) {
  return fbOnIdTokenChanged(auth, callback);
}

// Create user with email and password
export async function createUserWithEmailAndPasswordAuth(email, password) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log("User created successfully:", userCredential.user);
    return userCredential;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
}

// Send email verification with optional actionCodeSettings. `redirectTo` is an optional
// internal path (e.g. a community link) that should be preserved through the verification
// link so the user lands back on it after confirming their email — since it's baked into
// the actual emailed link, this is the only mechanism that survives cross-tab/cross-device.
export function getEmailVerificationActionCodeSettings(redirectTo) {
  const base =
    (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_APP_URL) ||
    (typeof window !== 'undefined' ? window.location.origin : '') ||
    '';
  const trimmed = String(base).replace(/\/$/, '');
  const params = new URLSearchParams({ mode: 'verifyEmail' });
  if (redirectTo) {
    params.set('redirectTo', redirectTo);
  }
  return {
    url: `${trimmed}/auth-action-handler?${params.toString()}`,
    handleCodeInApp: true,
  };
}

/**
 * @param {User} user
 * @param {ActionCodeSettings | null} [actionCodeSettings]
 */
export async function sendEmailVerificationAuth(user, actionCodeSettings = null) {
  try {
    // If no actionCodeSettings is provided, use default settings
    const settings = actionCodeSettings || getEmailVerificationActionCodeSettings();
    
    await sendEmailVerification(user, settings);
    console.log("Email verification sent successfully!");
  } catch (error) {
    console.error("Error sending email verification:", error);
    throw error;
  }
}

/**
 * Send password reset email with action code settings
 * @param {string} email - The user's email address
 * @param {Object|null} [actionCodeSettings=null] - Optional settings for the reset email
 * @param {string} actionCodeSettings.url - The URL to redirect to after action
 * @param {boolean} actionCodeSettings.handleCodeInApp - Whether to handle the code in-app
 * @returns {Promise<void>}
 */
// Send password reset email with actionCodeSettings
export async function sendPasswordResetEmailAuth(email, actionCodeSettings = null) {
  try {
    // If no actionCodeSettings is provided, use default settings
    const settings = actionCodeSettings || {
      url: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth-action-handler?mode=resetPassword`,
      handleCodeInApp: true,
    };
    
    await sendPasswordResetEmail(auth, email, settings);
    console.log("Password reset email sent successfully!");
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
}

// Update user profile
export async function updateUserProfile(user, profileData) {
  try {
    await updateProfile(user, profileData);
    console.log("User profile updated successfully!");
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
}

// Apply action code (for email verification)
export async function applyActionCodeAuth(actionCode) {
  try {
    await applyActionCode(auth, actionCode);
    console.log("Action code applied successfully!");
  } catch (error) {
    console.error("Error applying action code:", error);
    throw error;
  }
}

// Check action code (get info about the action code)
export async function checkActionCodeAuth(actionCode) {
  try {
    const info = await checkActionCode(auth, actionCode);
    console.log("Action code info:", info);
    return info;
  } catch (error) {
    console.error("Error checking action code:", error);
    throw error;
  }
}
